<template>
  <div class="container grid-xl">
    <ul class="step">
      <li class="step-item" :class="{ 'active': isCategory('upload') }">
        <a href="#" class="tooltip" data-tooltip="Upload photos">Upload</a>
      </li>
      <li class="step-item" :class="{ 'active': isCategory('align') }">
        <a href="#" class="tooltip" data-tooltip="Align photos">Align</a>
      </li>
      <li class="step-item" :class="{ 'active': isCategory('draw') }">
        <a href="#" class="tooltip" data-tooltip="Draw curves">Draw</a>
      </li>
      <li class="step-item" :class="{ 'active': isCategory('design') }">
        <a href="#" class="tooltip" data-tooltip="Design">Design</a>
      </li>
    </ul>

    <div class="columns">
      <div class="column col-8">
        <Upload v-if="isCategory('upload')" />

        <div id="canvas" v-if="!isCategory('upload')">
          <v-stage ref="stage"
                   :config="configKonva"
                   @wheel="stageZoom"
                   @mousedown="stageMouseDown"
                   style="background-color: #999">
            <v-layer>
              <v-image ref="extraOralRef"
                       :config="extraOralImage"
                       @mousedown="extraOralMouseDown"
                       @dragend="extraOralDragEnd"></v-image>

              <v-image ref="intraOralRef"
                       :config="intraOralImage"
                       @mousedown="intraOralMouseDown"
                       @click="intraOralClicked"
                       @dragend="intraOralDragEnd"></v-image>

              <v-line :config="horizontalLine"></v-line>

              <v-line :config="verticalLine"></v-line>

              <v-rect :config="regionOfInterest"></v-rect>

              <v-line :config="referenceLine"></v-line>

              <v-line :config="zenithCurve"></v-line>
              <v-circle v-for="(point, index) in zenithPoints" :key="'z' + index"
                        :config="point"
                        @dragend="zenithPointDragEnd($event, index)"></v-circle>

              <v-line :config="incisalEdgeCurve"></v-line>
              <v-circle v-for="(point, index) in incisalEdgePoints" :key="'i' + index"
                        :config="point"
                        @dragend="incisalEdgeDragEnd($event, index)"></v-circle>

              <v-line v-for="(line, index) in contourLines" :key="'c' + index" :config="line"></v-line>
              <template v-for="(points, toothIndex) in contourPoints">
                <v-circle v-for="(point, index) in points" :key="'c' + toothIndex + '_' + index"
                          :config="point"
                          @dragend="contourPointDragEnd($event, toothIndex, index)"></v-circle>
              </template>

              <v-line v-for="(line, index) in planLines" :key="'p' + index" :config="line"></v-line>
              <template v-for="(points, toothIndex) in planPoints">
                <v-circle v-for="(point, index) in points" :key="'p' + toothIndex + '_' + index"
                          :config="point"
                          @dragend="planPointDragEnd($event, toothIndex, index)"></v-circle>
              </template>

              <v-line v-for="(line, index) in toothAxisLines" :key="'ta' + index" :config="line"></v-line>

<!--               <v-line :config="referenceLengthLine"></v-line> -->

              <v-transformer ref="transformer"
                             :config="transformerConfig"
                             @transformend="transformerDragEnd"></v-transformer>
            </v-layer>
          </v-stage>
        </div>
      </div>

      <Sidebar/>
    </div>

    <!-- Design hints -->
    <div class="columns">
      <div v-if="isCategory('design')" class="column col-6 col-ml-auto shadow mt-2">
        <StatsPanel />
      </div>

      <div v-if="!isCategory('upload')" class="column col-4 col-ml-auto shadow mt-2">
        <Controls/>
      </div>
    </div>
  </div>
</template>

<script>
import StatsPanel from "@/components/smile/StatsPanel"
import Sidebar from "@/components/smile/Sidebar"
import Controls from "@/components/smile/Controls"
import Upload from "@/components/smile/Upload"

export const CANVAS_WIDTH = 848
export const CANVAS_HEIGHT = 565

const POINT_RADIUS = 5
const LINE_THICKNESS = 2.5
const REF_LINE_THICKNESS = 2.5
const REF_LINE_THICKNESS_INACTIVE = 2

export default {
  name: 'SmileDesign',
  components: {
    Upload,
    Controls,
    Sidebar,
    StatsPanel,
  },
  computed: {
    // State methods
    currentState () {
      return this.$store.state.currentState
    },

    // Align objects
    extraOralImage () {
      return Object.assign({
          draggable: this.currentState.matches('alignExtraOral'),
        },
        this.$store.state.extraOralImage
      )
    },

    intraOralImage () {
      return Object.assign({
          opacity: this.$store.state.intraOralOpacity / 100,
          draggable: this.currentState.matches('alignIntraOral'),
          visible: !this.currentState.matches('alignExtraOral'),
        },
        this.$store.state.intraOralImage)
    },

    horizontalLine () {
      return {
        x: 0,
        y: CANVAS_HEIGHT / 3,
        points: [-CANVAS_WIDTH * 4, 0, CANVAS_WIDTH * 5, 0],
        stroke: 'yellow',
        strokeWidth: (this.isCategory('align') ? REF_LINE_THICKNESS : REF_LINE_THICKNESS_INACTIVE)
                / this.configKonva.scale.x,
        opacity: 0.5,
        visible: this.$store.state.showReferenceLines
      }
    },

    verticalLine () {
      return {
        x: CANVAS_WIDTH / 2,
        y: 0,
        points: [0, -CANVAS_HEIGHT * 4, 0, CANVAS_HEIGHT * 5],
        stroke: 'yellow',
        strokeWidth: (this.isCategory('align') ? REF_LINE_THICKNESS : REF_LINE_THICKNESS_INACTIVE)
                / this.configKonva.scale.x,
        opacity: 0.5,
        visible: this.$store.state.showReferenceLines,
      }
    },

    referenceLine () {
      return {
        x: 0,
        y: 3 * CANVAS_HEIGHT / 4,
        points: [-CANVAS_WIDTH * 4, 0, CANVAS_WIDTH * 4, 0],
        stroke: 'yellow',
        strokeWidth: (this.isCategory('align') ? REF_LINE_THICKNESS * 4 : REF_LINE_THICKNESS_INACTIVE)
                / this.configKonva.scale.x,
        opacity: 0.5,
        visible: !this.currentState.matches('alignExtraOral')
                && !this.currentState.matches('alignIntraOral')
                && this.$store.state.showReferenceLines,

        draggable: this.currentState.matches('alignReferenceLine'),
        dragBoundFunc: (pos) => {
          return {
            x: 20,
            y: Math.max(pos.y, CANVAS_HEIGHT / 3)
          }
        }
      }
    },

    regionOfInterest () {
      return Object.assign({
                visible: this.currentState.matches('alignExtraOral'),
              },
              this.align.regionOfInterestRect)
    },

    /*
     * Draw objects
     */

    // Points
    zenithPoints () {
      return this.$store.state.zenithCoords.map((coords) => {
        return Object.assign({
          x: coords.x + this.$store.state.intraOralImage.x,
          y: coords.y + this.$store.state.intraOralImage.y,

          radius: POINT_RADIUS / this.configKonva.scale.x,
          strokeWidth: POINT_RADIUS * 0.25 / this.configKonva.scale.x,

          visible: this.currentState.matches('drawZenith'),
          draggable: this.currentState.matches('drawZenith'),
        }, this.draw.zenithCircle)
      })
    },

    incisalEdgePoints () {
      return this.$store.state.incisalEdgeCoords.map((coords) => {
        return Object.assign({
          x: coords.x + this.$store.state.intraOralImage.x,
          y: coords.y + this.$store.state.intraOralImage.y,

          radius: POINT_RADIUS / this.configKonva.scale.x,
          strokeWidth: POINT_RADIUS * 0.25 / this.configKonva.scale.x,

          visible: this.currentState.matches('drawIncisalEdge'),
          draggable: this.currentState.matches('drawIncisalEdge'),
        }, this.draw.incisalEdgeCircle)
      })
    },

    // Derived shapes
    zenithCurve () {
      const flattenedPoints = this.$store.state.zenithCoords.flatMap((coords) => [
        coords.x + this.$store.state.intraOralImage.x,
        coords.y + this.$store.state.intraOralImage.y
      ])

      return Object.assign({
        points: flattenedPoints,

        strokeWidth: LINE_THICKNESS / this.configKonva.scale.x,

        opacity: this.currentState.matches('design') ? 0.5 : 1,

        visible: this.currentState.matches('design') && this.$store.state.showOriginalCurvesAndAxes
      }, this.draw.zenithCurve)
    },

    incisalEdgeCurve () {
      const flattenedPoints = this.$store.state.incisalEdgeCoords.flatMap((coords) => [
        coords.x + this.$store.state.intraOralImage.x,
        coords.y + this.$store.state.intraOralImage.y
      ])

      return Object.assign({
        points: flattenedPoints,

        strokeWidth: LINE_THICKNESS / this.configKonva.scale.x,

        opacity: this.currentState.matches('design') ? 0.5 : 1,

        visible: this.currentState.matches('design') && this.$store.state.showOriginalCurvesAndAxes
      }, this.draw.zenithCurve)
    },

    toothAxisLines () {
      return this.$store.state.zenithCoords.map((zenithCoord, index) => {
        let points = []

        const incisalEdgeCoord = this.$store.state.incisalEdgeCoords[index]



        if (incisalEdgeCoord !== undefined) {
          points = [
            zenithCoord.x + this.$store.state.intraOralImage.x,
            zenithCoord.y + this.$store.state.intraOralImage.y,
            incisalEdgeCoord.x + this.$store.state.intraOralImage.x,
            incisalEdgeCoord.y + this.$store.state.intraOralImage.y,
          ]
        }

        return Object.assign({
          points: points,

          strokeWidth: LINE_THICKNESS / this.configKonva.scale.x,

          opacity: this.currentState.matches('design') ? 0.5 : 1,

          visible: this.currentState.matches('design') && this.$store.state.showOriginalCurvesAndAxes
        }, this.design.toothAxisLine)
      })
    },

    // Contour points and lines
    contourPoints () {
      return this.$store.state.contourCoords.map((contour, index) => {
        return contour.map((coords) => {
          return Object.assign({
            x: coords.x + this.$store.state.intraOralImage.x,
            y: coords.y + this.$store.state.intraOralImage.y,

            radius: POINT_RADIUS / this.configKonva.scale.x,
            strokeWidth: POINT_RADIUS * 0.25 / this.configKonva.scale.x,

            visible: this.currentState.matches('drawContour') && index === this.$store.state.activeContour,
            draggable: this.currentState.matches('drawContour'),
          }, this.draw.contourCircle)
        })
      })
    },

    contourLines () {
      return this.$store.state.contourCoords.map((coords, index) => {
        const flattenedPoints = coords.flatMap((coords) => [
          coords.x + this.$store.state.intraOralImage.x,
          coords.y + this.$store.state.intraOralImage.y
        ])

        return Object.assign({
          points: flattenedPoints,

          strokeWidth: LINE_THICKNESS / this.configKonva.scale.x,

          opacity: this.currentState.matches('design') ? 0.5 : 1,

          visible: (this.currentState.matches('drawContour') && index !== this.$store.state.activeContour)
            || (this.currentState.matches('design') && this.$store.state.showOriginalContours),
        }, this.draw.contourLine)
      })
    },

    /*
     * Design
     */

    // Points
    planPoints () {
      return this.$store.state.planCoords.map((contour) => {
        return contour.map((coords) => {
          return Object.assign({
            x: coords.x + this.$store.state.intraOralImage.x,
            y: coords.y + this.$store.state.intraOralImage.y,

            radius: POINT_RADIUS / this.configKonva.scale.x,
            strokeWidth: POINT_RADIUS * 0.25 / this.configKonva.scale.x,

            visible: this.currentState.matches('design') && this.$store.state.showPlanPoints,
            draggable: this.currentState.matches('design'),
          }, this.design.planCircle)
        })
      })
    },

    // Derived shapes
    planLines () {
      return this.$store.state.planCoords.map((coords) => {
        const flattenedPoints = coords.flatMap((coords) => [
          coords.x + this.$store.state.intraOralImage.x,
          coords.y + this.$store.state.intraOralImage.y
        ])

        return Object.assign({
          points: flattenedPoints,

          strokeWidth: LINE_THICKNESS / this.configKonva.scale.x,

          visible: this.currentState.matches('design'),
        }, this.design.planLine)
      })
    },

    // referenceLengthLine () {
    //   // Tooth 11
    //   let zenith = this.$store.state.zenithCoords[2]
    //   let incisalEdge = this.$store.state.incisalEdgeCoords[2]
    //
    //   if (zenith === undefined) {
    //     zenith = { x: 0, y: 0 }
    //   }
    //
    //   if (incisalEdge === undefined) {
    //     incisalEdge = { x: 0, y: 0 }
    //   }
    //
    //   const xAverage = (zenith.x + incisalEdge.x) / 2
    //
    //   return {
    //     x: this.$store.state.intraOralImage.x,
    //     y: this.$store.state.intraOralImage.y,
    //     points: [
    //       xAverage, zenith.y,
    //       xAverage, incisalEdge.y
    //     ],
    //     stroke: 'lightgreen',
    //     strokeWidth: 5
    //   }
    // },
  },
  data() {
    return {
      toothNumbers: [ 13, 12, 11, 21, 22, 23 ],

      configKonva: {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        x: 0,
        y: 0,
        scale: { x: 1, y: 1 },
      },

      transformerConfig: {
        centeredScaling: true,
        enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right']
      },

      align: {
        regionOfInterestRect: {
          x: CANVAS_WIDTH * 0.33, // 1/3
          y: CANVAS_HEIGHT * 0.625, // 5/8

          width: CANVAS_WIDTH * 0.33, // 1/3
          height: CANVAS_HEIGHT * 0.25, // 2/8

          fill: 'white',
          opacity: 0.2,
        },
      },

      draw: {
        zenithCircle: {
          fill: 'dodgerblue',
          stroke: "white",
        },
        zenithCurve: {
          x: 0,
          y: 0,
          tension: 0.5,
          closed: false,
          stroke: 'white',
        },

        incisalEdgeCircle: {
          fill: "limegreen",
          stroke: "white",
        },

        contourCircle: {
          fill: "white",
          stroke: "black",
        },

        contourLine: {
          x: 0,
          y: 0,
          tension: 0.5,
          closed: true,
          stroke: 'white',
        },
      },

      design: {
        referenceLength: '',

        planCircle: {
          fill: 'navajowhite',
          stroke: 'black',
        },

        planLine: {
          x: 0,
          y: 0,
          tension: 0.5,
          closed: true,
          stroke: 'orange',
        },

        toothAxisLine: {
          x: 0,
          y: 0,
          closed: false,
          stroke: 'white',
        }
      },

      startCoords: { x: 0, y: 0 },

      selectedShapeName: ''
    };
  },
  methods: {
    // State functions
    isCategory(name) {
      return this.$store.getters.isCategory(name)
      // return true
    },

    /*
     * Canvas event listeners
     */

    // On scroll, zoom in on teeth rectangle
    stageZoom(e) {
      e.evt.preventDefault();

      const stage = this.$refs.stage.getStage()

      const scaleBy = 1.05
      const oldScale = stage.scaleX()

      const newScale = e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy
      this.configKonva.scale = { x: newScale, y: newScale }

      // const mousePointTo = {
      //   x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
      //   y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale
      // }

      // this.configKonva.x = -(mousePointTo.x - stage.getPointerPosition().x / newScale) * newScale
      // this.configKonva.y = -(mousePointTo.y - stage.getPointerPosition().y / newScale) * newScale

      const centerX = CANVAS_WIDTH * 0.5
      const centerY = CANVAS_HEIGHT * 0.8

      const mousePointTo = {
        x: centerX / oldScale - stage.x() / oldScale,
        y: centerY / oldScale - stage.y() / oldScale
      }

      this.configKonva.x = -(mousePointTo.x - centerX / newScale) * newScale
      this.configKonva.y = -(mousePointTo.y - centerY / newScale) * newScale

      stage.batchDraw()
    },

    // Transformer node
    stageMouseDown(e) {
      // clear selection and return if stage is clicked
      if (e.target === e.target.getStage()) {
        this._disableTransformer()
      }
    },

    extraOralMouseDown(event) {
      if (this.currentState.matches('alignExtraOral')) {
        this._enableTransformer(event.target.name())
      } else {
        this._disableTransformer()
      }
    },

    intraOralMouseDown(event) {
      if (this.currentState.matches('alignIntraOral')) {
        this._enableTransformer(event.target.name())
      } else {
        this._disableTransformer()
      }
    },

    _enableTransformer(name) {
      this.selectedShapeName = name
      this.updateTransformer()
    },

    _disableTransformer() {
      this.selectedShapeName = ''
      this.updateTransformer()
    },

    transformerDragEnd() {
      const transformerNode = this.$refs.transformer.getStage()

      const transform = {
        rotation: transformerNode.node().rotation(),
        scaleX: transformerNode.node().scaleX(),
        scaleY: transformerNode.node().scaleY()
      }

      if (this.currentState.matches('alignExtraOral')) {
        this.$store.commit("updateExtraOralImage", transform)
      } else if (this.currentState.matches('alignIntraOral')) {
        this.$store.commit("updateIntraOralImage", transform)
      }
    },

    updateTransformer() {
      // here we need to manually attach or detach Transformer node
      const transformerNode = this.$refs.transformer.getStage();
      const stage = transformerNode.getStage();
      const selectedNode = stage.findOne('.' + this.selectedShapeName);

      // do nothing if selected node is already attached
      if (selectedNode === transformerNode.node()) {
        return;
      }

      if (selectedNode) {
        // attach to another node
        transformerNode.attachTo(selectedNode);
      } else {
        // remove transformer
        transformerNode.detach();
      }
      transformerNode.getLayer().batchDraw();
    },

    extraOralDragEnd(event) {
      this.$store.commit("updateExtraOralImage", {
        x: event.target.getPosition().x,
        y: event.target.getPosition().y,
      })
    },

    intraOralDragEnd(event) {
      this.$store.commit("updateIntraOralImage", {
        x: event.target.getPosition().x,
        y: event.target.getPosition().y,
      })
    },

    // Add points, depending on which state we are in
    intraOralClicked() {
      const stage = this.$refs.stage.getStage()

      const transform = stage.getAbsoluteTransform().copy()
      // to detect relative position we need to invert transform
      transform.invert()
      // now we find relative point
      const mousePos = transform.point( stage.getPointerPosition() )

      if (this.currentState.matches("drawZenith")) {
        this.$store.commit("addZenithCoord", {
          x: mousePos.x - this.$store.state.intraOralImage.x,
          y: mousePos.y - this.$store.state.intraOralImage.y
        })
      } else if (this.currentState.matches("drawIncisalEdge")) {
        this.$store.commit("addIncisalEdgeCoord", {
          x: mousePos.x - this.$store.state.intraOralImage.x,
          y: mousePos.y - this.$store.state.intraOralImage.y
        })
      } else if (this.currentState.matches("drawContour")) {
        this.$store.commit("addContourCoord", {
          tooth: this.$store.state.activeContour,
          x: mousePos.x - this.$store.state.intraOralImage.x,
          y: mousePos.y - this.$store.state.intraOralImage.y
        })
      }
    },


    // Drag already existing points around
    zenithPointDragEnd (event, index) {
      const endCoords = event.target.getPosition()
      this.$store.commit("updateZenithCoord", {
        index: index,
        x: endCoords.x - this.$store.state.intraOralImage.x,
        y: endCoords.y - this.$store.state.intraOralImage.y,
      })
    },

    incisalEdgeDragEnd (event, index) {
      const endCoords = event.target.getPosition()
      this.$store.commit("updateIncisalEdgeCoord", {
        index: index,
        x: endCoords.x - this.$store.state.intraOralImage.x,
        y: endCoords.y - this.$store.state.intraOralImage.y,
      })
    },

    contourPointDragEnd (event, tooth, index) {
      const endCoords = event.target.getPosition()
      this.$store.commit("updateContourCoord", {
        tooth: tooth,
        index: index,
        x: endCoords.x - this.$store.state.intraOralImage.x,
        y: endCoords.y - this.$store.state.intraOralImage.y,
      })
    },

    planPointDragEnd (event, tooth, index) {
      const endCoords = event.target.getPosition()
      this.$store.commit("updatePlanCoord", {
        tooth: tooth,
        index: index,
        x: endCoords.x - this.$store.state.intraOralImage.x,
        y: endCoords.y - this.$store.state.intraOralImage.y,
      })
    },
  }
}
</script>

<style scoped lang="scss">
#upload {
  height: 565px;

  display:flex;
  flex-direction: column;
  justify-content: space-around;
}

.shadow {
  box-shadow: 0 0 3px #aaa;
  border-radius: 5px;
}

.step {
  margin: 2rem 0;
}
</style>